/**
 * Formatting utilities for dates, numbers, and currency based on locale
 */

/**
 * Format a date according to the specified locale
 * @param {Date|string|number} date - The date to format
 * @param {string} locale - The locale code (e.g., 'en-US', 'tr-TR')
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, locale = 'en-US', options = {}) => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) return '';
  
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  };
  
  try {
    return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateObj.toLocaleDateString();
  }
};

/**
 * Format a date as a short date string (MM/DD/YYYY or DD.MM.YYYY)
 * @param {Date|string|number} date - The date to format
 * @param {string} locale - The locale code
 * @returns {string} Formatted short date string
 */
export const formatShortDate = (date, locale = 'en-US') => {
  return formatDate(date, locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * Format a date with time
 * @param {Date|string|number} date - The date to format
 * @param {string} locale - The locale code
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date, locale = 'en-US') => {
  return formatDate(date, locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format a date for list/table usage with localized long date + time
 * @param {Date|string|number} date
 * @param {string} language
 * @returns {string}
 */
export const formatListDate = (date, language = 'en') => {
  const locale = getLocaleFromLanguage(language);
  return formatDate(date, locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format a numeric date string based on language
 * @param {Date|string|number} date
 * @param {string} language
 * @returns {string}
 */
export const formatNumericDate = (date, language = 'en') => {
  const locale = getLocaleFromLanguage(language);
  return formatDate(date, locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * Format a number according to the specified locale
 * @param {number} number - The number to format
 * @param {string} locale - The locale code
 * @param {Object} options - Intl.NumberFormat options
 * @returns {string} Formatted number string
 */
export const formatNumber = (number, locale = 'en-US', options = {}) => {
  if (number === null || number === undefined || isNaN(number)) return '0';
  
  try {
    return new Intl.NumberFormat(locale, options).format(number);
  } catch (error) {
    console.error('Error formatting number:', error);
    return number.toString();
  }
};

/**
 * Format a currency value
 * @param {number} amount - The amount to format
 * @param {string} locale - The locale code
 * @param {string} currency - The currency code (e.g., 'USD', 'TRY')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, locale = 'en-US', currency = 'USD') => {
  // Default currency based on locale
  const defaultCurrency = locale === 'tr-TR' ? 'TRY' : 'USD';
  const currencyCode = currency || defaultCurrency;
  
  return formatNumber(amount, locale, {
    style: 'currency',
    currency: currencyCode,
  });
};

/**
 * Format a number as a percentage
 * @param {number} value - The value to format (e.g., 0.25 for 25%)
 * @param {string} locale - The locale code
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, locale = 'en-US', decimals = 0) => {
  return formatNumber(value, locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format a number with compact notation (e.g., 1K, 1.5M)
 * @param {number} number - The number to format
 * @param {string} locale - The locale code
 * @returns {string} Formatted compact number string
 */
export const formatCompactNumber = (number, locale = 'en-US') => {
  return formatNumber(number, locale, {
    notation: 'compact',
    compactDisplay: 'short',
  });
};

/**
 * Get relative time string (e.g., "2 hours ago", "in 3 days")
 * @param {Date|string|number} date - The date to compare
 * @param {string} locale - The locale code
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date, locale = 'en-US') => {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) return '';
  
  const now = new Date();
  const diffInSeconds = Math.floor((now - dateObj) / 1000);
  
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  
  // Seconds
  if (Math.abs(diffInSeconds) < 60) {
    return rtf.format(-diffInSeconds, 'second');
  }
  
  // Minutes
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (Math.abs(diffInMinutes) < 60) {
    return rtf.format(-diffInMinutes, 'minute');
  }
  
  // Hours
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (Math.abs(diffInHours) < 24) {
    return rtf.format(-diffInHours, 'hour');
  }
  
  // Days
  const diffInDays = Math.floor(diffInHours / 24);
  if (Math.abs(diffInDays) < 30) {
    return rtf.format(-diffInDays, 'day');
  }
  
  // Months
  const diffInMonths = Math.floor(diffInDays / 30);
  if (Math.abs(diffInMonths) < 12) {
    return rtf.format(-diffInMonths, 'month');
  }
  
  // Years
  const diffInYears = Math.floor(diffInMonths / 12);
  return rtf.format(-diffInYears, 'year');
};

/**
 * Get locale from i18next language code
 * @param {string} language - The language code (e.g., 'en', 'tr')
 * @returns {string} The locale code (e.g., 'en-US', 'tr-TR')
 */
export const getLocaleFromLanguage = (language) => {
  const localeMap = {
    en: 'en-US',
    tr: 'tr-TR',
  };
  return localeMap[language] || 'en-US';
};

/**
 * Hook-friendly formatters that use i18next language
 */
export const createI18nFormatters = (language) => {
  const locale = getLocaleFromLanguage(language);
  
  return {
    formatDate: (date, options) => formatDate(date, locale, options),
    formatShortDate: (date) => formatShortDate(date, locale),
    formatDateTime: (date) => formatDateTime(date, locale),
    formatListDate: (date) => formatListDate(date, language),
    formatNumericDate: (date) => formatNumericDate(date, language),
    formatNumber: (number, options) => formatNumber(number, locale, options),
    formatCurrency: (amount, currency) => formatCurrency(amount, locale, currency),
    formatPercentage: (value, decimals) => formatPercentage(value, locale, decimals),
    formatCompactNumber: (number) => formatCompactNumber(number, locale),
    formatRelativeTime: (date) => formatRelativeTime(date, locale),
  };
};

/**
 * Custom hook to get formatters for the current language
 * Usage: const { formatDate, formatNumber } = useFormatters();
 */
export const useFormatters = () => {
  // This will be imported from react-i18next in components
  // For now, we export the factory function
  return createI18nFormatters;
};

const formatters = {
  formatDate,
  formatShortDate,
  formatDateTime,
  formatListDate,
  formatNumericDate,
  formatNumber,
  formatCurrency,
  formatPercentage,
  formatCompactNumber,
  formatRelativeTime,
  getLocaleFromLanguage,
  createI18nFormatters,
  useFormatters,
};
export default formatters;
